import React, { useEffect, useState } from "react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import { Save, Calculator, MapPin, Loader } from "lucide-react";


function calcFee(config, straightKm) {
  const base    = Number(config.basePrice);
  const perKm   = Number(config.pricePerKm);
  const free    = Number(config.freeKm);
  const factor  = Number(config.roadFactor);
  const maxKm   = config.maxKm ? Number(config.maxKm) : null;
  const roadKm  = straightKm * factor;
  if (maxKm && roadKm > maxKm) return { fee: null, roadKm, outOfRange: true };
  const fee = Math.round(base + Math.max(0, roadKm - free) * perKm);
  return { fee, roadKm, outOfRange: false };
}

export default function DeliveryPricingManager() {
  const [, setConfig] = useState(null);
  const [form,    setForm]    = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [loading, setLoading] = useState(true);

  // Calculator state
  const [calcKm, setCalcKm] = useState("5");
  const calcResult = form && calcKm ? calcFee(form, Number(calcKm)) : null;

  useEffect(() => {
    API.get("/delivery-pricing")
      .then((res) => {
        const cfg = res.data?.data?.config ?? res.data?.data ?? res.data;
        setConfig(cfg);
        setForm({
          name:       cfg.name       ?? "Tarifa estándar",
          basePrice:  cfg.basePrice  ?? 3000,
          pricePerKm: cfg.pricePerKm ?? 500,
          freeKm:     cfg.freeKm     ?? 1,
          maxKm:      cfg.maxKm      ?? "",
          roadFactor: cfg.roadFactor ?? 1.30,
        });
      })
      .catch(() => toast.error("Error cargando configuración"))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await API.put("/delivery-pricing", {
        ...form,
        basePrice:  Number(form.basePrice),
        pricePerKm: Number(form.pricePerKm),
        freeKm:     Number(form.freeKm),
        maxKm:      form.maxKm !== "" ? Number(form.maxKm) : null,
        roadFactor: Number(form.roadFactor),
      });
      const updated = res.data?.data?.config ?? res.data?.data;
      if (updated) setConfig(updated);
      toast.success("Configuración guardada");
    } catch {
      toast.error("Error guardando configuración");
    } finally {
      setSaving(false);
    }
  };

  const field = (label, key, type = "number", hint = "") => (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1">{label}</label>
      {hint && <p className="text-xs text-gray-400 mb-1">{hint}</p>}
      <input
        type={type}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-supernova-400 text-sm"
      />
    </div>
  );

  if (loading) return (
    <div className="flex justify-center py-20">
      <Loader className="animate-spin text-supernova-500" size={32} />
    </div>
  );

  if (!form) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <MapPin className="text-supernova-600" size={26} />
          Tarifa de domicilio por distancia
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          La tarifa se calcula automáticamente al confirmar el despacho, usando la distancia entre la sede y la dirección del cliente.
        </p>
      </div>

      {/* Formulario */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
        <h3 className="font-bold text-gray-700">Configuración</h3>

        {field("Nombre de la tarifa", "name", "text")}

        <div className="grid grid-cols-2 gap-4">
          {field(
            "Tarifa base ($)",
            "basePrice",
            "number",
            "Costo mínimo de cualquier domicilio"
          )}
          {field(
            "Precio por km adicional ($)",
            "pricePerKm",
            "number",
            "Costo por cada km que supere los km gratis"
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          {field(
            "Km incluidos en la base",
            "freeKm",
            "number",
            "Los primeros N km están incluidos en la tarifa base"
          )}
          {field(
            "Cobertura máxima (km, vacío = sin límite)",
            "maxKm",
            "number",
            "Distancia máxima de cobertura"
          )}
        </div>

        {field(
          "Factor de carretera",
          "roadFactor",
          "number",
          "Multiplica la distancia en línea recta para estimar la distancia real recorrida (ej: 1.30 = +30%)"
        )}

        {/* Fórmula visual */}
        <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600 space-y-1 border border-gray-200">
          <p className="font-semibold text-gray-700 mb-2">Fórmula aplicada:</p>
          <p>
            <span className="font-mono bg-white px-2 py-0.5 rounded border border-gray-200 text-xs">
              distancia_real = distancia_linea_recta × {Number(form.roadFactor).toFixed(2)}
            </span>
          </p>
          <p>
            <span className="font-mono bg-white px-2 py-0.5 rounded border border-gray-200 text-xs">
              tarifa = ${Number(form.basePrice).toLocaleString("es-CO")} + max(0, distancia_real − {Number(form.freeKm)} km) × ${Number(form.pricePerKm).toLocaleString("es-CO")}/km
            </span>
          </p>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 bg-supernova-500 text-white py-3 rounded-xl font-bold hover:bg-supernova-600 transition disabled:opacity-60"
        >
          {saving ? <Loader size={18} className="animate-spin" /> : <Save size={18} />}
          Guardar configuración
        </button>
      </div>

      {/* Calculadora preview */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
        <h3 className="font-bold text-gray-700 flex items-center gap-2">
          <Calculator size={18} className="text-supernova-600" />
          Calculadora de prueba
        </h3>
        <p className="text-sm text-gray-500">
          Ingresa una distancia en línea recta para ver cuánto se cobraría.
        </p>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-500 mb-1">Distancia en línea recta (km)</label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={calcKm}
              onChange={(e) => setCalcKm(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
            />
          </div>
          {calcResult && (
            <div className={`text-center px-6 py-3 rounded-xl ${calcResult.outOfRange ? "bg-red-50 border border-red-200" : "bg-green-50 border border-green-200"}`}>
              {calcResult.outOfRange ? (
                <>
                  <p className="text-xs text-red-500 font-semibold">Fuera de cobertura</p>
                  <p className="text-sm text-red-600">{calcResult.roadKm.toFixed(2)} km reales</p>
                </>
              ) : (
                <>
                  <p className="text-xs text-gray-500">Tarifa estimada</p>
                  <p className="text-2xl font-black text-supernova-600">
                    ${calcResult.fee.toLocaleString("es-CO")}
                  </p>
                  <p className="text-xs text-gray-400">{calcResult.roadKm.toFixed(2)} km de recorrido</p>
                </>
              )}
            </div>
          )}
        </div>

        {/* Tabla de ejemplos */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="text-xs text-gray-400 uppercase border-b">
                <th className="pb-2 font-semibold">Distancia recta</th>
                <th className="pb-2 font-semibold">Recorrido estimado</th>
                <th className="pb-2 font-semibold text-right">Tarifa</th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 5, 7, 10, 15].map((km) => {
                const r = calcFee(form, km);
                return (
                  <tr key={km} className="border-b last:border-0">
                    <td className="py-2 text-gray-600">{km} km</td>
                    <td className="py-2 text-gray-500">{r.roadKm.toFixed(1)} km</td>
                    <td className="py-2 text-right font-semibold">
                      {r.outOfRange
                        ? <span className="text-red-400 text-xs">Sin cobertura</span>
                        : <span className="text-supernova-600">${r.fee.toLocaleString("es-CO")}</span>
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
